(function () {
  const SUPABASE_SELECTOR_BASE =
    "https://lnrqxocbpuoftlokhfpq.supabase.co/functions/v1/vehicle-selector";
  const NHTSA_BASE = "https://vpic.nhtsa.dot.gov/api/vehicles";
  const MIN_YEAR = 1980;
  const MODEL_VEHICLE_TYPES = [
    "passenger car",
    "multipurpose passenger vehicle (mpv)",
    "truck",
  ];

  function dedupeSorted(values) {
    return Array.from(
      new Set(
        values
          .map(function (value) {
            return String(value || "").trim();
          })
          .filter(Boolean)
      )
    ).sort(function (a, b) {
      return a.localeCompare(b);
    });
  }

  function dedupeSortedYears(values) {
    return Array.from(
      new Set(
        values
          .map(function (value) {
            return Number(value);
          })
          .filter(function (value) {
            return Number.isFinite(value);
          })
      )
    )
      .sort(function (a, b) {
        return b - a;
      })
      .map(function (value) {
        return String(value);
      });
  }

  function parseSearchPreset() {
    const params = new URLSearchParams(window.location.search);
    const q = params.get("q");
    if (!q) return null;

    const decoded = q.replace(/\+/g, " ");
    const quotedMatches = [];
    decoded.replace(/"([^"]+)"/g, function (_full, match) {
      quotedMatches.push(match.trim());
      return "";
    });

    if (quotedMatches.length >= 3) {
      return {
        year: quotedMatches[0],
        make: quotedMatches[1],
        model: quotedMatches.slice(2).join(" "),
      };
    }

    return null;
  }

  function setSelectOptions(select, placeholder, values, selectedValue) {
    select.innerHTML = "";

    const placeholderOption = document.createElement("option");
    placeholderOption.value = "";
    placeholderOption.textContent = placeholder;
    placeholderOption.disabled = true;
    placeholderOption.selected = true;
    select.appendChild(placeholderOption);

    values.forEach(function (value) {
      const option = document.createElement("option");
      option.value = value;
      option.textContent = value;
      if (selectedValue && value === selectedValue) {
        option.selected = true;
        placeholderOption.selected = false;
      }
      select.appendChild(option);
    });
  }

  function fetchJson(url) {
    return fetch(url, { credentials: "omit" }).then(function (response) {
      if (!response.ok) {
        throw new Error("HTTP " + response.status);
      }
      return response.json();
    });
  }

  function extractNhtsaModels(payload) {
    return dedupeSorted(
      ((payload && payload.Results) || [])
        .map(function (row) {
          return String((row && row.Model_Name) || "").trim();
        })
        .filter(Boolean)
    );
  }

  function extractNhtsaMakes(payload) {
    return dedupeSorted(
      ((payload && payload.Results) || [])
        .map(function (row) {
          return String((row && row.MakeName) || "").trim();
        })
        .filter(Boolean)
    );
  }

  function buildSearchUrl(year, make, model) {
    const params = new URLSearchParams();
    params.set("q", '"' + year + '" "' + make + '" "' + model + '"');
    params.set("type", "product");
    params.set("options[prefix]", "last");
    return "/search?" + params.toString();
  }

  async function fetchSupabaseYears() {
    const payload = await fetchJson(SUPABASE_SELECTOR_BASE + "/years");
    return dedupeSortedYears(Array.isArray(payload) ? payload : []);
  }

  async function fetchSupabaseMakes(year) {
    const payload = await fetchJson(
      SUPABASE_SELECTOR_BASE + "/makes?year=" + encodeURIComponent(year)
    );
    return dedupeSorted(Array.isArray(payload) ? payload : []);
  }

  async function fetchSupabaseModels(year, make) {
    const payload = await fetchJson(
      SUPABASE_SELECTOR_BASE +
        "/models?year=" +
        encodeURIComponent(year) +
        "&make=" +
        encodeURIComponent(make)
    );
    return dedupeSorted(Array.isArray(payload) ? payload : []);
  }

  async function fetchNhtsaMakes() {
    const makesPayload = await fetchJson(
      NHTSA_BASE + "/GetMakesForVehicleType/car?format=json"
    );
    return extractNhtsaMakes(makesPayload);
  }

  async function fetchNhtsaModels(year, make) {
    const typedLists = await Promise.all(
      MODEL_VEHICLE_TYPES.map(function (vehicleType) {
        const typedUrl =
          NHTSA_BASE +
          "/GetModelsForMakeYear/make/" +
          encodeURIComponent(make) +
          "/modelyear/" +
          encodeURIComponent(year) +
          "/vehicletype/" +
          encodeURIComponent(vehicleType) +
          "?format=json";

        return fetchJson(typedUrl)
          .then(extractNhtsaModels)
          .catch(function () {
            return [];
          });
      })
    );

    let models = dedupeSorted(
      typedLists.reduce(function (acc, list) {
        return acc.concat(list);
      }, [])
    );

    if (!models.length) {
      const fallbackUrl =
        NHTSA_BASE +
        "/GetModelsForMakeYear/make/" +
        encodeURIComponent(make) +
        "/modelyear/" +
        encodeURIComponent(year) +
        "?format=json";
      models = extractNhtsaModels(await fetchJson(fallbackUrl));
    }

    return models;
  }

  async function init() {
    const form = document.getElementById("car-selection-form");
    const yearNode = document.getElementById("year-select");
    const makeNode = document.getElementById("make-select");
    const modelNode = document.getElementById("model-select");

    if (!form || !yearNode || !makeNode || !modelNode) return;
    if (!(yearNode instanceof HTMLSelectElement)) return;
    if (!(makeNode instanceof HTMLSelectElement)) return;
    if (!(modelNode instanceof HTMLSelectElement)) return;

    const yearSelect = yearNode.cloneNode(false);
    const makeSelect = makeNode.cloneNode(false);
    const modelSelect = modelNode.cloneNode(false);
    yearSelect.id = "year-select";
    makeSelect.id = "make-select";
    modelSelect.id = "model-select";
    yearSelect.name = yearNode.name || "year";
    makeSelect.name = makeNode.name || "make";
    modelSelect.name = modelNode.name || "model";
    yearSelect.required = true;
    makeSelect.required = true;
    modelSelect.required = true;
    yearNode.replaceWith(yearSelect);
    makeNode.replaceWith(makeSelect);
    modelNode.replaceWith(modelSelect);

    const currentYear = new Date().getFullYear() + 1;
    const fallbackYears = [];
    for (let year = currentYear; year >= MIN_YEAR; year -= 1) {
      fallbackYears.push(String(year));
    }

    const makeCache = new Map();
    const modelCache = new Map();
    let nhtsaMakeCache = null;

    setSelectOptions(yearSelect, "Year", fallbackYears, "");
    setSelectOptions(makeSelect, "Make", [], "");
    setSelectOptions(modelSelect, "Model", [], "");
    makeSelect.disabled = true;
    modelSelect.disabled = true;

    try {
      const years = await fetchSupabaseYears();
      if (years.length) {
        setSelectOptions(yearSelect, "Year", years, "");
      }
    } catch (error) {
      console.warn("vehicle-lookup-override: supabase years fetch failed", error);
    }

    async function loadMakesForYear(year, selectedMake) {
      if (!year) {
        setSelectOptions(makeSelect, "Make", [], "");
        setSelectOptions(modelSelect, "Model", [], "");
        makeSelect.disabled = true;
        modelSelect.disabled = true;
        return;
      }

      let makes = makeCache.get(year);
      if (!makes) {
        let supabaseMakes = [];
        try {
          supabaseMakes = await fetchSupabaseMakes(year);
        } catch (error) {
          console.warn("vehicle-lookup-override: supabase makes fetch failed", error);
        }

        if (!nhtsaMakeCache) {
          try {
            nhtsaMakeCache = await fetchNhtsaMakes();
          } catch (error) {
            console.warn("vehicle-lookup-override: nhtsa makes fetch failed", error);
            nhtsaMakeCache = [];
          }
        }

        makes = dedupeSorted(supabaseMakes.concat(nhtsaMakeCache || []));
        makeCache.set(year, makes);
      }

      if (
        selectedMake &&
        makes.indexOf(selectedMake) === -1
      ) {
        makes = dedupeSorted(makes.concat([selectedMake]));
      }

      setSelectOptions(makeSelect, "Make", makes, selectedMake || "");
      makeSelect.disabled = makes.length === 0;
      setSelectOptions(modelSelect, "Model", [], "");
      modelSelect.disabled = true;
    }

    async function loadModelsFor(year, make, selectedModel) {
      if (!year || !make) {
        setSelectOptions(modelSelect, "Model", [], "");
        modelSelect.disabled = true;
        return;
      }

      const cacheKey = year + "|" + make.toUpperCase();
      let models = modelCache.get(cacheKey);
      if (!models) {
        let supabaseModels = [];
        let nhtsaModels = [];

        try {
          supabaseModels = await fetchSupabaseModels(year, make);
        } catch (error) {
          console.warn("vehicle-lookup-override: supabase models fetch failed", error);
        }

        try {
          nhtsaModels = await fetchNhtsaModels(year, make);
        } catch (error) {
          console.warn("vehicle-lookup-override: nhtsa models fetch failed", error);
        }

        models = dedupeSorted(supabaseModels.concat(nhtsaModels));
        modelCache.set(cacheKey, models);
      }

      if (
        selectedModel &&
        models.indexOf(selectedModel) === -1
      ) {
        models = dedupeSorted(models.concat([selectedModel]));
      }

      setSelectOptions(modelSelect, "Model", models, selectedModel || "");
      modelSelect.disabled = models.length === 0;
    }

    yearSelect.addEventListener("change", function () {
      loadMakesForYear(yearSelect.value, "").catch(function (error) {
        console.warn("vehicle-lookup-override: load makes failed", error);
      });
    });

    makeSelect.addEventListener("change", function () {
      loadModelsFor(yearSelect.value, makeSelect.value, "").catch(function (error) {
        console.warn("vehicle-lookup-override: load models failed", error);
      });
    });

    form.addEventListener(
      "submit",
      function (event) {
        if (!yearSelect.value || !makeSelect.value || !modelSelect.value) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        window.location.href = buildSearchUrl(
          yearSelect.value,
          makeSelect.value,
          modelSelect.value
        );
      },
      true
    );

    const preset = parseSearchPreset();
    if (!preset || !preset.year) return;

    yearSelect.value = String(preset.year);
    await loadMakesForYear(yearSelect.value, preset.make || "");

    if (preset.make) {
      await loadModelsFor(yearSelect.value, makeSelect.value, preset.model || "");
    }
  }

  document.addEventListener("DOMContentLoaded", function () {
    init().catch(function (error) {
      console.warn("vehicle-lookup-override: init failed", error);
    });
  });
})();
